import { addDays, isBefore, isSameDay, startOfDay } from "date-fns";
import { chunkArray } from "./array";
import { getGroupedDates } from "./dates";

interface ComputeBlockParams {
  clickedDate: Date;
  blockSize: number;
  fromDate: Date | undefined;
  toDate: Date | undefined;
  excludedDates: Date[];
  existingBlocks: Date[][];
}

export const computeBlock = ({
  clickedDate,
  blockSize,
  fromDate,
  toDate,
  excludedDates,
  existingBlocks,
}: ComputeBlockParams): Date[] => {
  let startDate = startOfDay(clickedDate);

  const nextBlockStart = existingBlocks
    .map((block) => startOfDay(block[0]))
    .filter((blockStart) => isBefore(startDate, blockStart))
    .sort((a, b) => a.getTime() - b.getTime())[0];

  if (nextBlockStart) {
    const blockEnd = addDays(startDate, blockSize - 1);
    if (!isBefore(blockEnd, nextBlockStart)) {
      startDate = addDays(nextBlockStart, -blockSize);
    }
  }

  if (toDate) {
    const maxStart = addDays(startOfDay(toDate), -(blockSize - 1));
    if (startDate > maxStart) {
      startDate = maxStart;
    }
  }

  if (fromDate && startDate < startOfDay(fromDate)) {
    startDate = startOfDay(fromDate);
  }

  const toDateNormalized = toDate ? startOfDay(toDate) : undefined;

  const block: Date[] = [];
  for (let i = 0; i < blockSize; i++) {
    const date = addDays(startDate, i);
    if (toDateNormalized && date > toDateNormalized) break;
    if (!excludedDates.some((d) => isSameDay(d, date))) {
      block.push(date);
    }
  }

  return block;
};

const buildBlock = (start: Date, size: number): Date[] =>
  Array.from({ length: size }, (_, i) => addDays(start, i));

export const splitIntoFixedBlocks = (groups: Date[][], blockSize: number): Date[][] => {
  if (blockSize <= 0) return groups;
  return groups.flatMap((group) => chunkArray(group, blockSize));
};

export const insertBlock = (
  existingDates: Date[],
  newBlock: Date[],
  blockSize: number,
  fromDate?: Date,
): Date[] => {
  if (newBlock.length === 0) return existingDates;

  const existingBlocks = splitIntoFixedBlocks(getGroupedDates(existingDates), blockSize)
    .sort((a, b) => a[0].getTime() - b[0].getTime());

  const newBlockStart = newBlock[0];
  const newBlockEnd = newBlock[newBlock.length - 1];

  const blocksBefore: Date[][] = [];
  const blocksAfter: Date[][] = [];

  for (const block of existingBlocks) {
    if (isBefore(block[0], newBlockStart)) {
      blocksBefore.push(block);
    } else {
      blocksAfter.push(block);
    }
  }

  const shiftedBefore: Date[][] = [];
  let backCursor = newBlockStart;
  for (let i = blocksBefore.length - 1; i >= 0; i--) {
    const block = blocksBefore[i];
    const blockEnd = block[block.length - 1];
    if (!isBefore(blockEnd, backCursor)) {
      const shiftedStart = addDays(backCursor, -blockSize);
      shiftedBefore.unshift(buildBlock(shiftedStart, blockSize));
      backCursor = shiftedStart;
    } else {
      shiftedBefore.unshift(block);
    }
  }

  const shiftedAfter: Date[][] = [];
  let forwardCursor = newBlockEnd;
  for (const block of blocksAfter) {
    if (!isBefore(forwardCursor, block[0])) {
      const shiftedStart = addDays(forwardCursor, 1);
      shiftedAfter.push(buildBlock(shiftedStart, blockSize));
      forwardCursor = addDays(shiftedStart, blockSize - 1);
    } else {
      shiftedAfter.push(block);
      forwardCursor = block[block.length - 1];
    }
  }

  if (fromDate) {
    const minDate = startOfDay(fromDate);
    const clamped = shiftedBefore.filter((block) => !isBefore(block[block.length - 1], minDate));
    return [...clamped.flat().filter((d) => !isBefore(d, minDate)), ...newBlock, ...shiftedAfter.flat()];
  }

  return [...shiftedBefore.flat(), ...newBlock, ...shiftedAfter.flat()];
};

interface AddBlockParams {
  clickedDate: Date;
  selectedDates: Date[];
  blockSize: number;
  fromDate: Date | undefined;
  toDate: Date | undefined;
  excludedDates: Date[];
}

export const addBlock = ({
  clickedDate,
  selectedDates,
  blockSize,
  fromDate,
  toDate,
  excludedDates,
}: AddBlockParams): Date[] => {
  const blocks = splitIntoFixedBlocks(getGroupedDates(selectedDates), blockSize);
  const newBlock = computeBlock({ clickedDate, blockSize, fromDate, toDate, excludedDates, existingBlocks: blocks });
  return insertBlock(selectedDates, newBlock, blockSize, fromDate);
};

export const findBlockContaining = (date: Date, blocks: Date[][]): number => {
  return blocks.findIndex((block) => block.some((d) => isSameDay(d, date)));
};
